import { CognitoUser } from 'amazon-cognito-identity-js';
import { useState } from 'react';

import { StyledMUIButton } from 'src/components/Button';
import { Toast, useTrigger } from 'src/components/Toast';

import { useTenant } from '@providers/Tenant';
import { useTranslation } from 'react-i18next';
import { error as logError } from 'src/services/log';
import { LoginPasswordInput } from '../TextInput/LoginPasswordInput';
import { LoginTextInput } from '../TextInput/LoginTextInput';
import LegalPolicies from './LegalPolicies';
import { StyledLink } from './styles';
import { LoginFormData, LoginFormProps } from './types';

export function LoginForm({ switchPage, setLoading, login, history, location }: LoginFormProps) {
  const [data, setData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const { t } = useTranslation('authentication');
  const { enabledFeatures } = useTenant();
  const partfulNextGen = enabledFeatures.includes('configuration-management');

  const [errorToast, setErrorToast] = useTrigger();
  const [errorToastMessage, setToastErrorMessage] = useState<string | undefined>();

  const validateBeforeSending = () => {
    const error = new Error();

    if (!data.password || !data.email) {
      error.name = 'FieldsEmpty';
      throw error;
    }
  };

  const showErrorToast = (message: string) => {
    setLoading(false);
    setToastErrorMessage(message);
    setErrorToast();
  };

  const onButtonClick = async (e: any, data: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      validateBeforeSending();
      await login(data.email, data.password).then(
        /* istanbul ignore next */ async (res: CognitoUser) => {
          if (res?.challengeName === 'NEW_PASSWORD_REQUIRED') {
            return history.push(`/create-password-with-temp-credentials?email=${encodeURIComponent(data.email)}`);
          }
          return history.push(location.state?.redirectUrl ?? '/product-library');
        }
      );
    } catch (e: any) {
      switch (e.name) {
        case 'NotAuthorizedException':
          showErrorToast(t('status.error.usernameOrPasswordIncorrect'));
          break;
        case 'FieldsEmpty':
          showErrorToast(t('status.error.fieldsCannotBeEmpty'));
          break;
        case 'UserLambdaValidationException':
          showErrorToast(t('status.error.emailAddressNotFound'));
          break;
        case 'InvalidEmailFormat':
          showErrorToast(t('status.error.emailProvidedNotValid'));
          break;
        /* istanbul ignore next */
        default:
          logError(e);
          showErrorToast(e.toString());
          break;
      }
    }
  };

  return (
    <>
      <form onSubmit={(e) => onButtonClick(e, data)} data-testid="login-form">
        <LoginTextInput
          label={t('form.email')}
          type="email"
          value={data.email}
          testId="login-email"
          onChange={(e: any) => setData((prevData) => ({ ...prevData, email: e.target.value }))}
          error={!!errorToast}
        />

        <LoginPasswordInput
          label={t('form.password')}
          value={data.password}
          testId="login-password"
          onChange={(e: any) => setData((prevData) => ({ ...prevData, password: e.target.value }))}
          error={!!errorToast}
        />

        <StyledLink onClick={/* istanbul ignore next */ () => switchPage()}>{t('form.forgotPassword')}</StyledLink>
        <StyledMUIButton type="submit" data-testid="login-submit" fullWidth>
          {t('form.login')}
        </StyledMUIButton>
      </form>

      {partfulNextGen && <LegalPolicies />}

      <Toast dependency={errorToast} severity="error" title="Error logging in" message={errorToastMessage || ''} />
    </>
  );
}
